import React from 'react';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * A reusable loader component that can be used in buttons or as a standalone component
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Whether the loader is active
 * @param {string} props.size - Size of the loader (small, medium, large)
 * @param {string} props.color - Color of the loader
 * @param {string} props.text - Text to display next to the loader
 * @param {boolean} props.fullScreen - Whether to display the loader fullscreen with overlay
 * @param {string} props.position - Position of the loader (left, right)
 * @param {React.ReactNode} props.children - Children to render when not loading
 * @param {Object} props.buttonProps - Props to pass to the button component
 */
const Loader = ({ 
  loading = false, 
  size = 'medium', 
  color = 'primary', 
  text = '', 
  fullScreen = false,
  position = 'left',
  children,
  buttonProps = {}
}) => {
  // Size mapping
  const sizeMap = {
    small: 16,
    medium: 24,
    large: 40
  };

  const circularProgressSize = sizeMap[size] || sizeMap.medium;

  // If fullScreen, render a fullscreen overlay with centered loader
  if (fullScreen) {
    return loading ? (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <CircularProgress size={circularProgressSize * 2} color={color} />
        {text && (
          <Typography
            variant="body1"
            color="white"
            sx={{ mt: 2 }}
          >
            {text}
          </Typography>
        )}
      </Box>
    ) : null;
  }

  // If used as a button loader
  if (buttonProps.variant) {
    return (
      <Button
        {...buttonProps}
        disabled={loading || buttonProps.disabled}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {position === 'left' && (
              <CircularProgress 
                size={circularProgressSize} 
                color="inherit" 
                sx={{ mr: text ? 1 : 0 }} 
              />
            )}
            {text && <span>{text}</span>}
            {position === 'right' && (
              <CircularProgress 
                size={circularProgressSize} 
                color="inherit" 
                sx={{ ml: text ? 1 : 0 }} 
              />
            )}
          </Box>
        ) : (
          children
        )}
      </Button>
    );
  }

  // Default inline loader
  return loading ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {position === 'left' && (
        <CircularProgress 
          size={circularProgressSize} 
          color={color} 
          sx={{ mr: text ? 1 : 0 }} 
        />
      )}
      {text && <Typography variant="body2" color="text.secondary">{text}</Typography>}
      {position === 'right' && (
        <CircularProgress 
          size={circularProgressSize} 
          color={color} 
          sx={{ ml: text ? 1 : 0 }} 
        />
      )}
    </Box>
  ) : (
    children || null
  );
};

Loader.propTypes = {
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  text: PropTypes.string,
  fullScreen: PropTypes.bool,
  position: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node,
  buttonProps: PropTypes.object
};

export default Loader;
